<template>
  <section class="section">
    <b-field class="regionSelector" label="Region">
      <b-select v-bind:value="this.currentSelected" style="margin-right: 15px" v-on:input="onChange">
        <option v-for="region in this.regions" :value="region.Name" :key="region.Name">{{ region.Name }}</option>
      </b-select>
    </b-field>
    <div class="container">
      <button class="button is-primary is-medium" @click="isModalActive = true">New Customer</button>
      <b-table
        :data="customers"
        :loading="loading"
        ref="table"
        paginated
        per-page="10"
        detailed
        detail-key="customerID"
        aria-next-label="Next page"
        aria-previous-label="Previous page"
        aria-page-label="Page"
        aria-current-label="Current page"
        @details-open="OpenDetail"
      >
        <b-table-column field="customerID" label="CustomerID" width="40" numeric v-slot="props">{{ props.row.customerID }}</b-table-column>

        <b-table-column field="customerName" label="Customer Name" sortable v-slot="props">{{ props.row.customerName }}</b-table-column>

        <b-table-column field="customerToken" label="CustomerToken" v-slot="props">
          {{ props.row.customerToken }}
        </b-table-column>

        <b-table-column label="Actions" v-slot="props">
          <b-button type="is-warning" @click="toggle(props.row)">Edit</b-button>
          <b-button type="is-danger" @click="confirmDeleteCustomer(props.row.customerID)">Delete</b-button>
        </b-table-column>

        <!-- Detail -->
        <template slot="detail" slot-scope="props">
          <div v-if="props.row.detailLoaded == true">
            <b-field grouped>
              <b-field label="CustomerName">
                <b-input v-model="props.row.customerName"></b-input>
              </b-field>
              <b-field label="CustomerToken" expanded>
                <b-input v-model="props.row.customerToken"></b-input>
              </b-field>
              <b-field label="Default Provider">
                <b-select placeholder="Select a provider" v-model="props.row.defaultProviderID">
                  <option v-for="provider in providers" :value="provider.providerID" :key="provider.providerID">{{ provider.providerName }}</option>
                </b-select>
              </b-field>
              <b-field style="display: flex; align-items: center">
                <b-button type="is-success" @click="updateCustomer(props.row.customerID)">Save</b-button>
                <b-button @click="toggle(props.row)">Close</b-button>
              </b-field>
            </b-field>
            <!-- Keys-->
            <b-field grouped v-for="(customerProvider, index) in getCustomerKeys(props.row.customerID)" :key="index">
              <b-field v-if="index == 0" label="Provider">
                <b-input v-model="customerProvider.providerName" disabled></b-input>
              </b-field>
              <b-field v-else label>
                <b-input v-model="customerProvider.providerName" disabled></b-input>
              </b-field>
              <b-field v-if="index == 0" label="Provider Key" expanded>
                <b-input v-model="customerProvider.providerKey"></b-input>
              </b-field>
              <b-field v-else label expanded>
                <b-input v-model="customerProvider.providerKey"></b-input>
              </b-field>
              <b-field style="display: flex; align-items: center">
                <b-button type="is-success" @click="addCustomerKey(props.row.customerID, customerProvider.providerID)">Save</b-button>
                <b-button type="is-danger" @click="confirmDeleteCustomerKey(props.row.customerID, customerProvider.providerKeyID)">Delete Key</b-button>
              </b-field>
            </b-field>
            <b-field grouped v-if="customerProviderMap.get(props.row.customerID).length < providers.length">
              <b-select placeholder="Select a provider" expanded :ref="props.row.customerID + '.AddProvider'">
                <option v-for="provider in getProvidersWithoutKeyCustomer(props.row.customerID)" :value="provider.providerID" :key="provider.providerID">{{ provider.providerName }}</option>
              </b-select>
              <b-button type="is-success" @click="addNewProviderKeyToMap(props.row.customerID)">Add Provider Key</b-button>
            </b-field>
          </div>
          <div v-else>
            <b-loading :is-full-page="false" :active="true"></b-loading>
          </div>
        </template>
      </b-table>
    </div>
    <b-modal :active.sync="isModalActive" :width="300" scroll="keep">
      <form v-on:submit.prevent="createCustomer">
        <div class="modal-card" style="width: auto">
          <header class="modal-card-head">
            <p class="modal-card-title">New Customer</p>
          </header>
          <section class="modal-card-body">
            <b-field label="Customer Name">
              <b-input type="text" v-model="formProps.name" placeholder="Customer Name" required></b-input>
            </b-field>

            <b-field label="Default Provider">
              <b-select placeholder="Select a provider" required expanded v-model="formProps.defaultprovider">
                <option v-for="provider in providers" :value="provider.providerID" :key="provider.providerID">{{ provider.providerName }}</option>
              </b-select>
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button class="button" type="button" @click="isModalActive = false">Close</button>
            <button class="button is-primary">Save</button>
          </footer>
        </div>
      </form>
    </b-modal>
  </section>
</template>

<script>
import axios from "axios";
import { ToastProgrammatic as Toast } from "buefy";
import ArrayHelper from "@/mixins/ArrayHelper";
import Provider from "../models/provider";
import Customer from "../models/customer";

const GlobalConfig = require("../config/index");

export default {
  mixins: [ArrayHelper],
  name: "Customers",
  data() {
    return {
      isModalActive: false,
      formProps: {},
      customers: [],
      providers: [],
      customerProviderMap: new Map(),
      customerProviderMapChangeTracker: 1,
      loading: true,
      regions: [],
      currentSelected: "",
    };
  },
  async beforeMount() {
    this.regions = GlobalConfig.default.API_SERVERS;
    this.currentSelected = GlobalConfig.default.GetAPIName();
    await this.GetCustomerData();
  },
  methods: {
    GetCustomerData: async function () {
      this.loading = true;
      this.customers = [];
      this.providers = [];
      this.customerProviderMap = new Map();
      try {
        const token = await this.$cognitoAuth.getIdToken();
        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };
        const providersGet = await axios.get(`${GlobalConfig.default.GetAPIURL()}/api/providers`, config);

        for (var providerObj of providersGet.data) {
          this.providers.push(new Provider(providerObj.ProviderID, providerObj.ProviderName, providerObj.DefaultLookupType));
        }

        const customersGet = await axios.get(`${GlobalConfig.default.GetAPIURL()}/api/customers`, config);

        for (var customer of customersGet.data) {
          // eslint-disable-next-line max-len
          this.customers.push(new Customer(customer.CustomerID, customer.CustomerName, customer.CustomerToken, -1));
        }

        this.loading = false;
      } catch (error) {
        console.error(error);
      }
    },
    async OpenDetail(customer) {
      const token = await this.$cognitoAuth.getIdToken();
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const customerKeys = await axios.get(`${GlobalConfig.default.GetAPIURL()}/api/customers/${customer.customerID}/keys`, config);

      // Check if map already contains customerKeys array - If not we add a empty one
      if (!this.customerProviderMap.has(customer.customerID)) {
        const initialArray = [];
        this.customerProviderMap.set(customer.customerID, initialArray);
      }
      for (var customerKey of customerKeys.data) {
        // Get customer keys array
        const array = this.customerProviderMap.get(customer.customerID);

        // Grab name and default lookup type from our list of providers we have already retrieved
        let providerNameRet = "";
        let defaultLookupType = "";
        const { KeyID, ProviderID, Key } = customerKey;

        for (var provider of this.providers) {
          if (provider.providerID === ProviderID) {
            providerNameRet = provider.providerName;
            defaultLookupType = provider.providerDefaultLookupType;
          }
        }

        // Create a new provider object, add the key to it then push it to the back of keys array
        const providerKey = new Provider(customerKey.ProviderID, providerNameRet, defaultLookupType);
        providerKey.providerKey = Key;
        providerKey.providerKeyID = KeyID;
        array.push(providerKey);
        this.customerProviderMap.set(customer.customerID, array);
      }
      this.$set(customer, "detailLoaded", true);
    },
    getCustomerKeys: function (customerID) {
      return this.customerProviderMapChangeTracker && this.customerProviderMap.get(customerID);
    },
    confirmDeleteCustomerKey: function (customerID, ProviderID) {
      this.$buefy.dialog.confirm({
        title: "Deleting Key",
        message: "Are you sure you want to <b>delete</b> this key? This action cannot be undone.",
        confirmText: "Delete Key",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.deleteCustomerKey(customerID, ProviderID),
      });
    },
    deleteCustomerKey: async function (customerID, KeyID) {
      const token = await this.$cognitoAuth.getIdToken();
      await axios.delete(`${GlobalConfig.default.GetAPIURL()}/api/customers/${customerID}/keys/${KeyID}`, { headers: { Authorization: `Bearer ${token}` } });

      let indexFound = -1;
      const customerArray = this.customerProviderMap.get(customerID);
      customerArray.forEach((provider, index) => {
        if (provider.providerKeyID === KeyID) {
          indexFound = index;
        }
      });

      customerArray.splice(indexFound, 1);
      this.customerProviderMap.set(customerID, customerArray);
      this.customerProviderMapChangeTracker += 1;

      Toast.open({
        message: "Customer key deleted!",
        type: "is-success",
      });
    },
    addCustomerKey: async function (customerID, providerID) {
      let provider = new Provider(-1, "", "");
      let found = false;
      this.customerProviderMap.get(customerID).forEach((customerProvider) => {
        if (customerProvider.providerID === providerID) {
          provider = customerProvider;
          found = true;
        }
      });

      if (found) {
        if (provider.providerKey) {
          const token = await this.$cognitoAuth.getIdToken();
          const config = {
            headers: { Authorization: `Bearer ${token}` },
          };

          const body = {
            ProviderID: provider.providerID,
            Key: provider.providerKey,
          };
          await axios.put(`${GlobalConfig.GetAPIURL()}/api/customers/${customerID}/keys`, body, config);
          Toast.open({
            message: "Provider key saved!",
            type: "is-success",
          });
        } else {
          Toast.open({
            message: "You have not entered a key!",
            type: "is-danger",
          });
        }
      }
    },
    getProvidersWithoutKeyCustomer: function (customerID) {
      const providersCopy = [...this.providers];
      if (this.customerProviderMap.has(customerID)) {
        this.customerProviderMap.get(customerID).forEach((customerProvider) => {
          providersCopy.forEach((provider, index) => {
            if (customerProvider.providerID === provider.providerID) {
              providersCopy.splice(index, 1);
            }
          });
        });
      }

      return providersCopy;
    },
    addNewProviderKeyToMap: function (customerID) {
      const ref = `${customerID}.AddProvider`;
      const refObj = this.$refs[ref];
      if (refObj.selected !== null) {
        let providerFound = null;

        this.providers.forEach((provider) => {
          if (provider.providerID === refObj.selected) {
            providerFound = provider;
          }
        });

        const array = this.customerProviderMap.get(customerID);
        if (providerFound !== null) {
          array.push(providerFound);
          this.customerProviderMap.set(customerID, array);
          this.customerProviderMapChangeTracker += 1;
        }
      } else {
        Toast.open({
          message: "You need to select a provider from the dropdown!",
          type: "is-warning",
        });
      }
    },
    confirmDeleteCustomer: function (customerID) {
      this.$buefy.dialog.confirm({
        title: "Deleting Key",
        message: "Are you sure you want to <b>delete</b> this customer? This action cannot be undone.",
        confirmText: "Delete Key",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.deleteCustomer(customerID),
      });
    },
    deleteCustomer: async function (customerID) {
      try {
        const token = await this.$cognitoAuth.getIdToken();
        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };
        await axios.delete(`${GlobalConfig.default.GetAPIURL()}/api/customers/${customerID}`, config);
        Toast.open({
          message: "Successfully deleted customer!",
          type: "is-success",
        });
        await this.GetCustomerData();
      } catch (error) {
        Toast.open({
          message: "An error occured deleting Customer record!",
          type: "is-danger",
        });
        // console.error(error);
      }
    },
    toggle: function (row) {
      this.$refs.table.toggleDetails(row);
    },
    updateCustomer: async function (CustomerID) {
      let customerFound = null;

      this.customers.forEach((customer) => {
        if (customer.customerID === CustomerID) {
          customerFound = customer;
        }
      });

      if (customerFound != null) {
        try {
          const token = await this.$cognitoAuth.getIdToken();
          const config = {
            headers: { Authorization: `Bearer ${token}` },
          };
          await axios.put(`${GlobalConfig.GetAPIURL()}/api/customers/${CustomerID}`, customerFound, config);
          Toast.open({
            message: "Successfully updated Customer record!",
            type: "is-success",
          });
        } catch (error) {
          Toast.open({
            message: "An error occured updating Customer record!",
            type: "is-danger",
          });
          console.error(error);
        }
      }
    },
    createCustomer: async function () {
      try {
        const customerData = {
          CustomerName: this.formProps.name,
          DefaultProviderID: this.formProps.defaultprovider,
        };
        const token = await this.$cognitoAuth.getIdToken();
        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };
        await axios.post(`${GlobalConfig.default.GetAPIURL()}/api/customers`, customerData, config);
        Toast.open({
          message: "Successfully created customer!",
          type: "is-success",
        });
        this.isModalActive = false;
        await this.GetCustomerData();
      } catch (error) {
        Toast.open({
          message: "An error occured adding customer!",
          type: "is-danger",
        });
        // console.error(error);
      }
    },
    onChange(changedTo) {
      localStorage.setItem("APIName", changedTo);
      this.currentSelected = GlobalConfig.default.GetAPIName();
      this.GetCustomerData();
    },
  },
};
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.section {
  position: relative;
}
.regionSelector {
  top: 10px;
  right: 10px;
  position: absolute;
}
</style>
